import { Box, Breadcrumbs, IconButton, Link, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AddButton from "../Components/Button/Add";
import ExportCsvButton from "../Components/Button/ExportCsvButton";
import CTM_Grid_Element from "../Components/CTM_Grid_Element";
import AddRunDialog from "../Components/Dialog/AddRunDialog";
import ConfirmDialog from "../Components/Dialog/ConfirmDialog";
import Loading from "../Components/Loading";
import TimeInput from "../Components/TimeInput";
import { CTM_Colors, IRunData, SessionGridRow, defaultFormData } from "../Helper";
import pb from "../pocketBase";

interface SessionDetailPageProps {
  sessionId: string;
}

interface IRunDataWithId extends IRunData {id: string, created: string; }

export const SessionDetailPage: React.FC<SessionDetailPageProps> = ({ sessionId }) => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<SessionGridRow | null>(null);
  const [runs, setRuns] = useState<IRunDataWithId[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Edit mode state
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingRunId, setEditingRunId] = useState<string | null>(null);
  const [editingRunData, setEditingRunData] = useState<IRunData>(defaultFormData);

  // Confirm dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [runToDelete, setRunToDelete] = useState<{ id: string; name: string } | null>(null);

  // Expanded software params state: Set of "runId-deviceName" keys
  const [expandedDevices, setExpandedDevices] = useState<Set<string>>(new Set());
  const PARAMS_COLLAPSE_THRESHOLD = 5;
  const toggleDevice = (runId: string, device: string) => {
    const key = `${runId}-${device}`;
    setExpandedDevices(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // important for auto fetch after adding a new run
  const fetchRuns = useCallback(async () => {
    try {
      const records = await pb.collection('Run').getFullList<IRunDataWithId>(200, {
        filter: `sessionId='${sessionId}'`,
        sort: '-created'
      });
      setRuns(records);
    } catch (err: any) {
      // Ignore auto-cancellation (normal PocketBase behavior)
      if (!err?.isAbort) {
        console.error('Error fetching runs:', err);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    const fetchSessionData = async () => {
      setIsLoading(true);
      try {
        const record = await pb.collection('TestingSession').getOne(sessionId);
        const sessionGridRow: SessionGridRow = {
          id: record.id,
          name: record.name,
          info: record.info,
          date: new Date(record.date)
        };
        setSessionData(sessionGridRow);
      } catch (err: any) {
        // Ignore auto-cancellation (normal PocketBase behavior)
        if (!err?.isAbort) {
          console.error('Error fetching session data:', err);
          toast.error('Failed to fetch session data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionData();
    fetchRuns();
  }, [sessionId, fetchRuns]);

  const handleOpenDialog = () => {
    setDialogMode('create');
    setEditingRunData(runs.length > 0 ? runs[0] : defaultFormData);
    setEditingRunId(null);
    setIsDialogOpen(true);
  }

  const handleEditRun = (run: IRunDataWithId) => {
    setDialogMode('edit');
    setEditingRunId(run.id);
    setEditingRunData(run);
    setIsDialogOpen(true);
  }

  const handleDeleteRun = (runId: string, runName: string) => {
    setRunToDelete({ id: runId, name: runName });
    setConfirmDialogOpen(true);
  };

  const confirmDeleteRun = async () => {
    if (!runToDelete) return;

    try {
      await pb.collection('Run').delete(runToDelete.id);
      setRuns((prevRuns) => prevRuns.filter(run => run.id !== runToDelete.id));
      if (expandedRunId === runToDelete.id) {
        setExpandedRunId(null);
      }
      toast.success('Run deleted successfully!');
    } catch (error) {
      console.error('Error deleting run:', error);
      toast.error('Error deleting run');
    } finally {
      setConfirmDialogOpen(false);
      setRunToDelete(null);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setDialogMode('create');
    setEditingRunId(null);
    setEditingRunData(defaultFormData);
  }

  const handleRunSaved = (savedRunId: string) => {
    fetchRuns().then(() => {
      setExpandedRunId(savedRunId);
    });
  };

  const ArrayToOutputString4 = (arr: number[]) => {
    return (`FL: ${arr[0]}, FR: ${arr[1]}, RL: ${arr[2]}, RR: ${arr[3]}`);
  }

  const ArrayToOutputString2 = (arr: number[]) => {
    return (`F: ${arr[0]}, R: ${arr[1]}`);
  }

  const formatRunTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const DisplayRuns: React.FC = () => {
    return (
      <>
        <Typography variant="h5" style={{ marginTop: '20px' }}>
          Runs
        </Typography>
        {
          runs.length > 0 ?
            <Box sx={{ padding: '20px', backgroundColor: `${CTM_Colors.ctm_green}80`, borderRadius: '8px' }}>
              {runs.map((run, index) => (
                <Box
                  key={`${run.event}-${run.driver}-${index}`}
                  sx={(theme) => ({
                    padding: '15px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    backgroundColor: theme.palette.mode === 'dark' ? CTM_Colors.dark_grey : '#fff',
                    ...(index !== runs.length - 1 && { marginBottom: '20px' }),
                  })}
              >
                  <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '98%',
                        cursor: 'pointer'
                      }}
                      onClick={() => setExpandedRunId(expandedRunId === run.id ? null : run.id)}
                    >
                      <Box>
                        <strong>Run #{runs.length - index}:</strong> {run.driver} - {run.event} - {run.runComment}
                      </Box>
                      <Box sx={{ color: CTM_Colors.ctm_green }}>
                        {formatRunTime(run.created)}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRun(run);
                        }}
                        sx={{ color: CTM_Colors.ctm_green }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRun(run.id, `${run.driver} - ${run.event}`);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedRunId(expandedRunId === run.id ? null : run.id)}
                      >
                        {expandedRunId === run.id ? '▲' : '▼'}
                      </Typography>
                    </Box>
                  </Box>

                  {expandedRunId === run.id && (
                      <Box sx={{ marginTop: '10px' }}>

                  <Grid container spacing={2} rowSpacing={1} columns={{ xs: 4, sm: 8, md: 12 }}>
                    <CTM_Grid_Element displayName="Description/Comments" value={run.info} size={{ xs: 4, sm: 8, md: 12 }} />
                    <CTM_Grid_Element displayName="Driver" value={run.driver} />
                    <CTM_Grid_Element displayName="Comment" value={run.runComment}/>
                    <CTM_Grid_Element displayName="Event" value={run.event} />
                    <CTM_Grid_Element displayName="Track Temperature (°C)" value={run.track_temperature} />
                    <CTM_Grid_Element displayName="Front Wing" value={run.frontwing} />
                    <CTM_Grid_Element displayName="Rear Wing" value={run.rearwing} />
                    <CTM_Grid_Element displayName="Undertray" value={run.undertray} />
                    <CTM_Grid_Element displayName="Ambient Temperature (°C)" value={run.ambient_temperature} />
                    <CTM_Grid_Element displayName="Ambient Humidity (%)" value={run.ambient_humidity} />
                    <CTM_Grid_Element displayName="Ambient Pressure (hPa)" value={run.ambient_pressure} />
                    <CTM_Grid_Element displayName="Sky Condition" value={run.sky_condition} />
                    <CTM_Grid_Element displayName="Precipitation Type" value={run.precipitationType} defaultValue={'none' as IRunData['precipitationType']} />
                    <CTM_Grid_Element displayName="Precipitation Amount" value={run.precipitationAmount} />
                    <Grid size={{ xs: 4, sm: 8, md: 12 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: CTM_Colors.ctm_green, mb: 1 }}>
                        Software
                      </Typography>
                      {run.software && typeof run.software === 'object' && Object.keys(run.software).length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.entries(run.software as Record<string, any>).map(([device, version]) => {
                            const params = run.software_params && typeof run.software_params === 'object'
                              ? (run.software_params as Record<string, any>)[device]
                              : null;
                            const isPlainStringParams = params && typeof params === 'string';
                            const paramEntries = params && typeof params === 'object' ? Object.entries(params) : [];
                            const collapsible = paramEntries.length > PARAMS_COLLAPSE_THRESHOLD;
                            const isExpanded = expandedDevices.has(`${run.id}-${device}`);
                            const visibleParams = collapsible && !isExpanded ? paramEntries.slice(0, PARAMS_COLLAPSE_THRESHOLD) : paramEntries;
                            return (
                              <Box key={device} sx={(theme) => ({
                                border: `1px solid ${CTM_Colors.ctm_green}60`,
                                borderRadius: '6px',
                                padding: '8px 12px',
                                minWidth: '200px',
                                flex: '1 1 200px',
                                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9',
                              })}>
                                <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: CTM_Colors.ctm_green, mb: 0.5 }}>
                                  {device}
                                </Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', mb: (paramEntries.length > 0 || isPlainStringParams) ? 1 : 0 }}>
                                  {String(version)}
                                </Typography>
                                {isPlainStringParams && (
                                  <Box sx={{ borderTop: `1px solid ${CTM_Colors.ctm_green}30`, pt: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.72rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{params}</Typography>
                                  </Box>
                                )}
                                {paramEntries.length > 0 && (
                                  <Box sx={{ borderTop: `1px solid ${CTM_Colors.ctm_green}30`, pt: 0.5 }}>
                                    {visibleParams.map(([key, val]) => (
                                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{key}</Typography>
                                        <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{String(val)}</Typography>
                                      </Box>
                                    ))}
                                    {collapsible && (
                                      <Typography
                                        onClick={() => toggleDevice(run.id, device)}
                                        sx={{ fontSize: '0.72rem', color: CTM_Colors.ctm_green, cursor: 'pointer', mt: 0.5, userSelect: 'none' }}
                                      >
                                        {isExpanded ? '▲ Hide details' : `▼ Show all ${paramEntries.length} params`}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography>-</Typography>
                      )}
                    </Grid>
                    <CTM_Grid_Element displayName="Camber IA" value={Array.isArray(run.camber_IA) ? ArrayToOutputString4(run.camber_IA) : '-'} />
                    <CTM_Grid_Element displayName="Tire Compound" value={run.tire_compound} />
                    <CTM_Grid_Element displayName="Tire Pressure" value={Array.isArray(run.tire_pressure) ? ArrayToOutputString4(run.tire_pressure) : '-'} />
                    <CTM_Grid_Element displayName="Tire Temperature (°C)" value={Array.isArray(run.tire_temperature) ? ArrayToOutputString4(run.tire_temperature) : '-'} />
                    <CTM_Grid_Element displayName="Toe" value={Array.isArray(run.toe) ? ArrayToOutputString4(run.toe) : '-'} />
                    <CTM_Grid_Element displayName="Springs" value={Array.isArray(run.springs) ? ArrayToOutputString4(run.springs) : '-'} />
                    <CTM_Grid_Element displayName="Damper Rebound" value={Array.isArray(run.damper_rebound) ? ArrayToOutputString2(run.damper_rebound) : '-'} />
                    <CTM_Grid_Element displayName="Damper Compression" value={Array.isArray(run.damper_compression) ? ArrayToOutputString2(run.damper_compression) : '-'} />
                    <CTM_Grid_Element displayName="ARB Setting" value={run.arb_setting} />
                    <CTM_Grid_Element displayName="Corner Weight" value={Array.isArray(run.corner_weight) ? ArrayToOutputString4(run.corner_weight) : '-'} />

                    {/* Additional Notes */}
                    <Grid size={{ xs: 12 }}>
                      <TimeInput runId={run.id}/>
                    </Grid>
                  </Grid>
                </Box>
                  )}
                </Box>
              ))}
            </Box> : (
              <Typography>No runs available for this session.</Typography>
            )
        }
      </>
    )
  }

  if (isLoading) {
    return <Loading message="Loading session details..." />;
  }

  return (
    <div>
      {sessionData ? (
        <div>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ marginTop: 2, marginBottom: 2 }}
          >
            <Link
              underline="hover"
              color="inherit"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{ cursor: 'pointer' }}
            >
              Sessions
            </Link>
            <Typography color={CTM_Colors.ctm_green} fontWeight="bold">
              {sessionData.name}
            </Typography>
          </Breadcrumbs>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2
          }}>
            <h2 style={{ margin: 0 }}>
              {`${sessionData.name} ${sessionData.date ? `(${new Date(sessionData.date).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })})` : ''}`}
            </h2>
            <ExportCsvButton sessionId={sessionId} />
          </Box>
          <p>{sessionData.info}</p>
          <DisplayRuns />
          <AddRunDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            mode={dialogMode}
            runId={editingRunId || undefined}
            initialData={editingRunData}
            onRunSaved={handleRunSaved}
          />
          <ConfirmDialog
            open={confirmDialogOpen}
            title="Delete Run"
            message={`Are you sure you want to delete "${runToDelete?.name}"? This will also delete all associated lap times and feedback.`}
            onConfirm={confirmDeleteRun}
            onCancel={() => setConfirmDialogOpen(false)}
            confirmText="Delete"
            cancelText="Cancel"
            confirmColor="error"
          />
          <AddButton openDialog={handleOpenDialog} />
        </div>
      ) : (
        <div>No session data found</div>
      )}
    </div>
  );
}

